"use client";

import {
  createContext,
  useContext,
  useMemo,
  Children,
  isValidElement,
  type ReactNode,
} from "react";
import { cn } from "@mdxui/primitives/lib/utils";
import { Separator } from "@mdxui/primitives";

/**
 * Context to track which column indices should be highlighted (.do columns)
 */
const HighlightedColumnsContext = createContext<Set<number>>(new Set());

/**
 * Context to provide column index to td elements
 */
const ColumnIndexContext = createContext<number>(0);

/**
 * Custom table components for ReactMarkdown that auto-highlight .do columns.
 * Usage: <ReactMarkdown components={markdownTableComponents}>
 *
 * This implementation uses React Context to track which columns contain ".do"
 * in their headers, then applies highlighting to both th and td cells in those columns.
 */
// Type helper for accessing props.children safely
interface ElementWithChildren {
  children?: ReactNode;
  node?: { tagName?: string };
}

export const markdownTableComponents = {
  hr: () => <Separator className="my-8" />,

  table: ({ children }: { children?: ReactNode }) => {
    // Scan children to find thead and extract highlighted column indices
    const highlightedColumns = useMemo(() => {
      const indices = new Set<number>();

      Children.forEach(children, (child) => {
        if (!isValidElement<ElementWithChildren>(child)) return;

        // Look for thead
        if (
          child.type === "thead" ||
          (child.props && child.props.node?.tagName === "thead")
        ) {
          // Find tr within thead
          Children.forEach(child.props.children, (trChild) => {
            if (!isValidElement<ElementWithChildren>(trChild)) return;

            // Find th elements within tr
            let colIndex = 0;
            Children.forEach(trChild.props.children, (thChild) => {
              if (!isValidElement<ElementWithChildren>(thChild)) return;

              const text = extractText(thChild.props.children);
              if (/\.do\b/i.test(text)) {
                indices.add(colIndex);
              }
              colIndex++;
            });
          });
        }
      });

      return indices;
    }, [children]);

    return (
      <HighlightedColumnsContext.Provider value={highlightedColumns}>
        <div className="mt-2 mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm min-w-[600px]">
            {children}
          </table>
        </div>
      </HighlightedColumnsContext.Provider>
    );
  },

  thead: ({ children }: { children?: ReactNode }) => {
    return <thead>{children}</thead>;
  },

  tbody: ({ children }: { children?: ReactNode }) => {
    return <tbody>{children}</tbody>;
  },

  tr: ({ children }: { children?: ReactNode }) => {
    // Wrap each child (th or td) with its column index
    const childrenWithIndex = Children.map(children, (child, index) => {
      if (!isValidElement(child)) return child;
      return (
        <ColumnIndexContext.Provider key={index.toString()} value={index}>
          {child}
        </ColumnIndexContext.Provider>
      );
    });

    return <tr>{childrenWithIndex}</tr>;
  },

  th: ({ children }: { children?: ReactNode }) => {
    const columnIndex = useContext(ColumnIndexContext);
    const highlightedColumns = useContext(HighlightedColumnsContext);

    // Check if this header contains .do (for initial render before context is set)
    const text = extractText(children);
    const isHighlightedByContent = /\.do\b/i.test(text);
    const isHighlightedByContext = highlightedColumns.has(columnIndex);
    const isHighlighted = isHighlightedByContent || isHighlightedByContext;

    return (
      <th
        className={cn(
          "px-4 py-3 text-left font-semibold border border-border",
          isHighlighted ? "bg-muted text-foreground" : "text-muted-foreground",
        )}
        data-do-column={isHighlighted ? "true" : undefined}
      >
        {children}
      </th>
    );
  },

  td: ({ children }: { children?: ReactNode }) => {
    const columnIndex = useContext(ColumnIndexContext);
    const highlightedColumns = useContext(HighlightedColumnsContext);
    const isHighlighted = highlightedColumns.has(columnIndex);

    return (
      <td
        className={cn(
          "px-4 py-3 border border-border",
          isHighlighted
            ? "bg-muted/50 text-foreground"
            : "text-muted-foreground",
        )}
      >
        {children}
      </td>
    );
  },
};

// Helper to extract text from React children
function extractText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return extractText(
      (children as { props?: { children?: ReactNode } }).props?.children,
    );
  }
  return "";
}
